import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { I18nBoundary } from '@/i18n/provider';
import { applySiteDefault, initLocale, isSiteKnown } from '@/i18n/runtime';
import { publicModelStatsQuery, publicModelsQuery, siteQuery } from '@/lib/queries';
import { queryClient } from '@/lib/query-client';
import { enableReveal } from '@/lib/reveal';
import { router } from '@/router';
import './styles.css';

enableReveal();

/** 应用首次渲染完成：取消预渲染页的“语言待定”隐藏（见 styles.css 与 scripts/prerender.mjs） */
function RenderedMarker() {
  useEffect(() => {
    document.documentElement.classList.remove('locale-pending');
  }, []);
  return null;
}

const container = document.getElementById('root');
if (!container) throw new Error('#root not found');

// 预渲染的公开页（scripts/prerender.mjs）：静态正文已经可见，先加载路由代码与公开数据（最多 1.5 秒）再接管，
// 避免静态内容被骨架屏替换一下
async function warmUp() {
  if (!container!.hasChildNodes()) return;
  await Promise.race([
    Promise.allSettled([
      router.load(),
      queryClient.prefetchQuery(siteQuery),
      queryClient.prefetchQuery(publicModelsQuery),
      queryClient.prefetchQuery(publicModelStatsQuery),
    ]),
    new Promise((r) => setTimeout(r, 1500)),
  ]);
}

/** 第一次访问还不知道是哪个站：最多等 1 秒站点信息，国内站直接用中文渲染，不按浏览器语言先闪一下别的语言 */
async function knowSite() {
  if (isSiteKnown()) return;
  await Promise.race([queryClient.prefetchQuery(siteQuery), new Promise((r) => setTimeout(r, 1000))]);
  const site = queryClient.getQueryData(siteQuery.queryKey);
  if (site) await applySiteDefault(site.site_id);
}

// 先确定语言并加载语言包，再渲染，避免首屏闪一下中文
void initLocale()
  .then(knowSite)
  .then(warmUp)
  .then(() => {
    createRoot(container).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <I18nBoundary>
            <RouterProvider router={router} />
            <RenderedMarker />
          </I18nBoundary>
        </QueryClientProvider>
      </StrictMode>,
    );
  });
